import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { isAxiosError } from 'axios';
import { ThumbsUp, Shuffle, X, ShieldCheck } from 'lucide-react';
import { dashboardApi, DASHBOARD_KEY, type LocatieType } from '../../services/professionalDashboardApi';
import { WERKPLEK_SIGNALEN_KEY } from '../ProfessionalDashboardSchil';
import type { WerkplekSignal } from '../../services/professionalWerkplekApi';
import { useOpmaak } from './opmaak';

const LOCATIES: LocatieType[] = ['ONLINE', 'WERKGEVER', 'EXTERN'];

const foutTekst = (err: unknown) => (isAxiosError(err) ? err.response?.data?.message : undefined) ?? (err as Error)?.message ?? '';

function TegenvoorstelDialoog({ afspraakId, onSluit }: { afspraakId: string; onSluit: () => void }) {
  const { t } = useTranslation('professionalDashboard');
  const queryClient = useQueryClient();
  const opmaak = useOpmaak();
  const { data, isLoading } = useQuery({ queryKey: ['afspraak-alternatieven', afspraakId], queryFn: () => dashboardApi.alternatieven(afspraakId) });
  const [start, setStart] = useState<string>('');
  const [locatie, setLocatie] = useState<LocatieType | ''>('');
  const [omschrijving, setOmschrijving] = useState('');

  const gekozenStart = start || data?.huidige_start || '';
  const gekozenLocatie = locatie || data?.huidige_locatie_type || 'ONLINE';
  const ongewijzigd = data && gekozenStart === data.huidige_start && gekozenLocatie === data.huidige_locatie_type && !omschrijving.trim();

  const versturen = useMutation({
    mutationFn: () =>
      dashboardApi.tegenvoorstel(afspraakId, {
        start_tijd: gekozenStart,
        locatie_type: gekozenLocatie,
        locatie_omschrijving: omschrijving.trim() || null,
      }),
    onSuccess: () => {
      toast.success(t('verzoek.dialoog.verstuurd'));
      queryClient.invalidateQueries({ queryKey: DASHBOARD_KEY });
      queryClient.invalidateQueries({ queryKey: WERKPLEK_SIGNALEN_KEY });
      onSluit();
    },
    onError: (err) => toast.error(t('verzoek.fout', { fout: foutTekst(err) })),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-navy-950/40 p-4" role="dialog" aria-modal="true" aria-labelledby="tegenvoorstel-titel">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div>
            <h3 id="tegenvoorstel-titel" className="font-semibold text-navy-900">{t('verzoek.dialoog.titel')}</h3>
            <p className="mt-1 text-xs text-navy-500">{t('verzoek.dialoog.uitleg')}</p>
          </div>
          <button type="button" onClick={onSluit} className="p-1 text-navy-400 hover:text-navy-900" aria-label={t('verzoek.dialoog.annuleren')}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4 px-5 py-4">
          <label className="block">
            <span className="text-xs font-semibold text-navy-700">{t('verzoek.dialoog.tijdstip')}</span>
            {isLoading ? (
              <div className="mt-1 h-10 rounded-lg bg-gray-50 animate-pulse" />
            ) : (
              <select
                value={gekozenStart}
                onChange={(e) => setStart(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-navy-500 focus:ring-1 focus:ring-navy-500"
              >
                {data && <option value={data.huidige_start}>{t('verzoek.dialoog.zelfdeTijdstip', { tijd: opmaak.datumTijd(data.huidige_start) })}</option>}
                {data?.starts.map((s) => (
                  <option key={s.start_tijd} value={s.start_tijd}>
                    {opmaak.datumTijd(s.start_tijd)} – {opmaak.tijd(s.eind_tijd)}
                  </option>
                ))}
              </select>
            )}
            {data && data.starts.length === 0 && <span className="mt-1 block text-xs text-amber-700">{t('verzoek.dialoog.geenSlots')}</span>}
          </label>
          <fieldset>
            <legend className="text-xs font-semibold text-navy-700">{t('verzoek.dialoog.locatie')}</legend>
            <div className="mt-1 grid grid-cols-3 gap-1.5">
              {LOCATIES.map((l) => (
                <button
                  key={l}
                  type="button"
                  aria-pressed={gekozenLocatie === l}
                  onClick={() => setLocatie(l)}
                  className={`rounded-lg border px-2 py-2 text-xs font-medium ${
                    gekozenLocatie === l ? 'border-navy-900 bg-navy-900 text-white' : 'border-gray-300 bg-white text-navy-700 hover:border-navy-300'
                  }`}
                >
                  {t(`locatie.${l}`)}
                </button>
              ))}
            </div>
          </fieldset>
          {gekozenLocatie !== 'ONLINE' && (
            <label className="block">
              <span className="text-xs font-semibold text-navy-700">{t('verzoek.dialoog.omschrijving')}</span>
              <input
                value={omschrijving}
                onChange={(e) => setOmschrijving(e.target.value)}
                maxLength={2000}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-navy-500 focus:ring-1 focus:ring-navy-500"
              />
            </label>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-3">
          <button type="button" onClick={onSluit} className="rounded-lg px-3 py-2 text-sm font-medium text-navy-700 hover:bg-navy-50">
            {t('verzoek.dialoog.annuleren')}
          </button>
          <button
            type="button"
            disabled={!gekozenStart || !!ongewijzigd || versturen.isPending}
            onClick={() => versturen.mutate()}
            className="rounded-lg bg-navy-900 px-4 py-2 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-50"
          >
            {t('verzoek.dialoog.versturen')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Afspraak-goedkeuringskaart: wie (casus), wat, wanneer, waar — en direct bevestigen of tegenvoorstel. */
export const AfspraakVerzoekKaart: React.FC<{ signaal: WerkplekSignal }> = ({ signaal }) => {
  const { t } = useTranslation('professionalDashboard');
  const queryClient = useQueryClient();
  const opmaak = useOpmaak();
  const [dialoog, setDialoog] = useState(false);
  const p = signaal.params;
  const afspraakId = String(p.afspraak_id);

  const goedkeuren = useMutation({
    mutationFn: () => dashboardApi.keurGoed(afspraakId),
    onSuccess: () => {
      toast.success(t('verzoek.bevestigd'));
      queryClient.invalidateQueries({ queryKey: DASHBOARD_KEY });
      queryClient.invalidateQueries({ queryKey: WERKPLEK_SIGNALEN_KEY });
    },
    onError: (err) => toast.error(t('verzoek.fout', { fout: foutTekst(err) })),
  });

  const rij = (label: string, waarde: React.ReactNode) => (
    <div className="grid grid-cols-[6.5rem_1fr] gap-2 py-1">
      <dt className="text-xs font-semibold text-navy-400">{label}</dt>
      <dd className="text-sm text-navy-900">{waarde}</dd>
    </div>
  );

  return (
    <article className="rounded-xl border border-gray-200 border-l-4 border-l-gold-400 bg-white p-3">
      <dl>
        {rij(
          t('verzoek.wie'),
          <span className="inline-flex items-center gap-1.5">
            {p.casus ? t('agenda.casus', { casus: p.casus }) : t('verzoek.casusOnbekend')}
            {Number(p.vertrouwelijk) === 1 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-navy-50 px-1.5 py-0.5 text-[11px] font-semibold text-navy-700">
                <ShieldCheck className="w-3 h-3" />
                {t('verzoek.vertrouwelijk')}
              </span>
            )}
          </span>,
        )}
        {rij(t('verzoek.wat'), t('verzoek.sessieNummer', { sessie: t(`agenda.sessie.${p.sessie_type}`, { defaultValue: String(p.sessie_type ?? '') }), nummer: p.sessie_nummer }))}
        {rij(t('verzoek.wanneer'), `${opmaak.datumTijd(String(p.datum))} – ${opmaak.tijd(String(p.eind))}`)}
        {rij(t('verzoek.waar'), [t(`locatie.${p.locatie_type}`, { defaultValue: String(p.locatie_type ?? '') }), p.locatie].filter(Boolean).join(' — '))}
      </dl>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          type="button"
          disabled={goedkeuren.isPending}
          onClick={() => goedkeuren.mutate()}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-navy-900 px-3 py-2 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-60"
        >
          <ThumbsUp className="w-4 h-4" />
          {t('verzoek.bevestigen')}
        </button>
        <button
          type="button"
          onClick={() => setDialoog(true)}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-navy-800 hover:border-navy-300"
        >
          <Shuffle className="w-4 h-4" />
          {t('verzoek.tegenvoorstel')}
        </button>
      </div>
      {dialoog && <TegenvoorstelDialoog afspraakId={afspraakId} onSluit={() => setDialoog(false)} />}
    </article>
  );
};
